/**
 * División político-administrativa de Colombia (DIVIPOLA)
 * -----------------------------------------------------------------------------
 * Departamentos y municipios con su código DANE, para los formularios de
 * dirección. Fuente: DANE, DIVIPOLA de junio de 2026.
 *
 *   const co = await CompanyTheme.colombia('/tema/data/colombia-divipola.json');
 *
 *   co.departamentos();          // [{ c: '05', n: 'ANTIOQUIA' }, …]  33
 *   co.municipios('05');         // los de Antioquia, ya ordenados
 *   co.municipio('05001');       // { c, n, d }
 *
 * **Los datos NO van dentro del bundle**, sino en un JSON aparte que se pide
 * cuando hace falta. Son 43 kB: incrustarlos los cargaría en todas las apps,
 * también en las que no tienen formularios de dirección y en las que no operan
 * en Colombia. Aquí solo está el lector.
 *
 * La URL se pasa siempre, no se adivina: el theme se sirve desde rutas distintas
 * en cada proyecto —`/tema/`, `/build/`, un CDN— y acertar por casualidad daría
 * un 404 difícil de rastrear.
 *
 * Los centros poblados —veredas y corregimientos, 7.456 registros— no se
 * empaquetan: son 386 kB y su sitio es una tabla de la aplicación con su
 * endpoint, como cualquier catálogo grande.
 */

const cache = new Map();

class Colombia {
  constructor(data) {
    this.data = data;

    // Índice por departamento, para no recorrer los 1.104 municipios cada vez
    // que se abre el desplegable.
    this._porDepto = new Map();
    (data.municipios || []).forEach((m) => {
      if (!this._porDepto.has(m.d)) this._porDepto.set(m.d, []);
      this._porDepto.get(m.d).push(m);
    });

    this._porCodigo = new Map((data.municipios || []).map((m) => [m.c, m]));
  }

  /** Los 33 departamentos, ordenados por nombre. */
  departamentos() {
    return this.data.departamentos || [];
  }

  /**
   * @param {string} codigoDepartamento Código DANE de dos dígitos.
   */
  municipios(codigoDepartamento) {
    return this._porDepto.get(String(codigoDepartamento)) || [];
  }

  /**
   * @param {string} codigo Código DANE de cinco dígitos.
   */
  municipio(codigo) {
    return this._porCodigo.get(String(codigo)) || null;
  }

  /** Pares `{ value, label }`, que es lo que esperan los desplegables. */
  opcionesDepartamentos() {
    return this.departamentos().map((d) => ({ value: d.c, label: d.n }));
  }

  opcionesMunicipios(codigoDepartamento) {
    return this.municipios(codigoDepartamento).map((m) => ({ value: m.c, label: m.n }));
  }
}

/**
 * Carga el JSON y devuelve el lector. La misma URL solo se pide una vez.
 *
 * @param {string} url Ruta del `colombia-divipola.json` publicado por la app.
 * @returns {Promise<Colombia>}
 */
export function colombia(url) {
  if (!url) throw new Error('company-theme: falta la URL de colombia-divipola.json');

  if (!cache.has(url)) {
    cache.set(
      url,
      fetch(url, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((data) => new Colombia(data))
        .catch((error) => {
          // Si falla, se olvida: reintentar no debería devolver el error viejo.
          cache.delete(url);
          throw error;
        })
    );
  }

  return cache.get(url);
}

export { Colombia };
export default colombia;
